// 🔑 THE REVIEW-TAB HEADER: WHERE THE BORROWER IS IN THE GUIDED FLOW (Jesse, 2026-10-23 — pre-switch-on push).
// "Step 5 of 8 · Income, assets and housing", when they reached it, and "N items still needed" — a COUNT only (Jesse,
// 2026-10-24 strip-back: "remove the duplicate open-items list from the step header … The cards below are the only
// list"). The items themselves, with the staff detail and the actions, are the tab's open-item cards. Joint files show
// each applicant. Built only from the shell's own state (GET /api/admin/borrowers/:id/guided-view) through the
// shell's own rules (constants/guidedRemaining) — no second calculation.
import React from 'react';
import { staffRemainingOnStep } from '../../constants/guidedRemaining';

const dayOf = (d) => {
  const t = d ? new Date(d) : null;
  return t && !Number.isNaN(t.getTime()) ? t.toLocaleDateString() : null;
};

// A need's source in staff words (the Review tab never says "ReadyCheck" — Jesse, 2026-10-24).
const SOURCE_LABELS = { readycheck: 'document check', readycheck_auto: 'document check', admin: 'staff', manual: 'staff' };
const sourceLabel = (s) => SOURCE_LABELS[String(s || '').toLowerCase()] || String(s || '').replace(/readycheck/gi, 'document check');

/** The staff detail line for one item. */
export function staffDetailLine(staff = {}) {
  if (staff.kind === 'documents') {
    const docs = (staff.documents || []).map((d) => (d.docType ? `${d.label} (${d.docType})` : d.label));
    return docs.length ? `Documents not provided: ${docs.join('; ')}` : 'Section not provided';
  }
  if (staff.kind === 'need') {
    return [`Need #${staff.needId}`, staff.category, staff.docType && `uploads as ${staff.docType}`, staff.source && `source ${sourceLabel(staff.source)}`,
      staff.staffText && `staff title "${staff.staffText}"`].filter(Boolean).join(' · ');
  }
  if (staff.kind === 'letter') {
    return [`Letter #${staff.letterId}`, staff.category, staff.description].filter(Boolean).join(' · ');
  }
  if (staff.kind === 'signature') return 'Program agreement not signed';
  if (staff.kind === 'gate') return `Gate: ${staff.code}`;
  if (staff.kind === 'step7') return `Step-7 screen: ${staff.phase}`;
  if (staff.kind === 'blockers') return staff.owner === 'staff' ? 'Staff\'s move' : 'The borrower\'s move — nothing for staff to do here';
  return '';
}

function ApplicantHeader({ applicant, joint }) {
  const name = [applicant.firstName, applicant.lastName].filter(Boolean).join(' ');
  // ⚠️ No step state to read (the payload carries none for this applicant): the name only — never a crash, and no
  // "not on the flow" state (cleanup Phase 2: there is one flow).
  if (!applicant.state) {
    return (
      <div data-testid="guided-header-applicant" style={{ flex: 1, minWidth: 0, fontSize: 12, color: '#64748B' }}>
        {joint && <div style={{ fontWeight: 700, color: '#111827' }}>{name}</div>}
      </div>
    );
  }
  const r = staffRemainingOnStep({ state: applicant.state, needs: applicant.needs, step5: applicant.step5, step7: applicant.step7, letters: applicant.letters, stepBlockers: applicant.stepBlockers });
  const reached = dayOf(r.reachedAt);
  return (
    <div data-testid="guided-header-applicant" data-step={r.step} style={{ flex: 1, minWidth: 0 }}>
      {joint && (
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
          {applicant.role === 'primary' ? 'Primary' : 'Co-borrower'} · {name}
        </div>
      )}
      <div data-testid="guided-header-step" style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>
        Step {r.step} of {r.totalSteps} · {r.label}
      </div>
      <div data-testid="guided-header-reached" style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>
        {reached ? `Reached ${reached}` : 'Reached: not recorded'}
      </div>
      {r.note && <div data-testid="guided-header-note" style={{ fontSize: 12.5, color: '#111827', marginTop: 8 }}>{r.note}</div>}
      {r.noteDetail && <div data-testid="guided-header-note-staff" style={{ fontSize: 11.5, color: '#64748B', marginTop: 2 }}>{r.noteDetail}</div>}
      {r.items.length > 0 && (
        <div data-testid="guided-header-count" data-count={r.items.length} style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginTop: 8 }}>
          {r.items.length} item{r.items.length === 1 ? '' : 's'} still needed
        </div>
      )}
      {r.items.length === 0 && !r.note && (
        <div style={{ fontSize: 12.5, color: '#111827', marginTop: 8 }}>Nothing is needed from the borrower on this step.</div>
      )}
    </div>
  );
}

export default function GuidedStepHeader({ view }) {
  if (!view || !Array.isArray(view.applicants) || !view.applicants.some((a) => a.guided)) return null;
  const joint = view.applicants.length > 1;
  return (
    <div data-testid="guided-step-header" style={{ background: '#ffffff', border: '2px solid #FFDD86', borderRadius: 12,
      padding: '14px 16px', marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        {view.applicants.map((a) => <ApplicantHeader key={a.userId} applicant={a} joint={joint} />)}
      </div>
    </div>
  );
}
